/**
 * vs-cart-drawer-integration.js
 *
 * Globally enhances product card / quick-add buttons to:
 *   1. Add via AJAX (no page reload)
 *   2. Open the existing Horizon cart drawer
 *
 * Behavior:
 *   - For products with NO variant choices (only default variant): adds directly to cart on click.
 *   - For products WITH variant choices: preserves Horizon's quick-add modal (variant picker)
 *     because the customer must pick a variant first. After they submit the modal form,
 *     Horizon's existing product-form-component already does AJAX + drawer.
 *
 * Compatibility:
 *   - Uses Horizon's documented ThemeEvents.cartUpdate ('cart:update') event.
 *   - Dispatches the same payload shape Horizon expects so the cart bubble & drawer update.
 *   - Calls drawer.showDialog() directly as a fallback when auto-open isn't enabled in settings.
 *   - Idempotent: safe to load multiple times; uses a marker class to prevent double-bind.
 *   - Does NOT touch existing markup or CSS. Pure runtime enhancement.
 *   - Aborts gracefully if Horizon's components aren't present (e.g. on pages without cart drawer).
 */
(function () {
  'use strict';

  if (window.__vsCartDrawerIntegrationLoaded) return;
  window.__vsCartDrawerIntegrationLoaded = true;

  var MARKER = 'data-vs-cart-bound';

  /**
   * Fetch the latest cart state from Shopify.
   */
  function fetchCart() {
    return fetch(window.Shopify ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { 'Accept': 'application/json' }
    }).then(function (r) { return r.json(); });
  }

  /**
   * Dispatch Horizon's cart update event and open the drawer.
   *
   * @param {Object} cart        - /cart.js response.
   * @param {string} sourceId    - Section id of the triggering card (debug).
   * @param {Object} [addResponse] - /cart/add.js response, carries the `sections` map Horizon needs.
   */
  function notifyCartUpdated(cart, sourceId, addResponse) {
    if (window.VsCartSync) {
      window.VsCartSync.notify({
        cart: cart,
        addResponse: addResponse,
        source: sourceId || 'vs-cart-drawer-integration'
      });
      window.VsCartSync.openDrawer();
      return;
    }

    // Fallback: still include `data.sections` so component-cart-items doesn't throw.
    var sections = (addResponse && addResponse.sections && typeof addResponse.sections === 'object')
      ? addResponse.sections
      : {};
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        source: sourceId || 'vs-cart-drawer-integration',
        sourceId: sourceId || 'vs-cart-drawer-integration',
        data: { source: 'vs-cart-drawer-integration', itemCount: cart.item_count, sections: sections }
      }
    }));

    // Explicitly open the cart drawer (handles cases where auto-open is off).
    var drawer = document.querySelector('cart-drawer-component, cart-drawer');
    if (drawer) {
      if (typeof drawer.showDialog === 'function') {
        drawer.showDialog();
      } else if (typeof drawer.open === 'function') {
        drawer.open();
      }
    }
  }

  /**
   * Show a non-blocking error indicator on the button.
   */
  function showButtonError(button, message) {
    if (!button) return;
    var original = button.getAttribute('aria-label') || '';
    button.setAttribute('data-vs-cart-error', 'true');
    button.setAttribute('aria-label', message || 'Toevoegen mislukt');
    setTimeout(function () {
      button.removeAttribute('data-vs-cart-error');
      if (original) button.setAttribute('aria-label', original);
    }, 2500);
    console.warn('[vs-cart-drawer-integration]', message);
  }

  /**
   * Determine whether the clicked button is for a product with NO variant choice
   * (i.e., only-default-variant). For those, we add directly; otherwise the modal opens.
   *
   * Strategy: look for a sibling element that exposes the variant count via a data attribute,
   * OR check the product card root for a flag we set during render.
   *
   * Fallback: if we can't determine, let the original click proceed (modal opens) — safer.
   */
  function getDirectAddVariantId(buttonEl) {
    // 1. Direct variant id on the button itself (custom sections may use this).
    var directVariantId = buttonEl.getAttribute('data-vs-direct-variant-id');
    if (directVariantId) return directVariantId;

    // 2. Look at the parent quick-add-component for a known single-variant flag.
    var quickAdd = buttonEl.closest('quick-add-component, [data-quick-add]');
    if (quickAdd && quickAdd.getAttribute('data-default-variant-id') && quickAdd.getAttribute('data-has-variants') === 'false') {
      return quickAdd.getAttribute('data-default-variant-id');
    }

    // 3. Look at product-card root — Horizon's product-card may expose a default variant.
    var card = buttonEl.closest('product-card, [data-product-card]');
    if (card) {
      var defaultVariantId = card.getAttribute('data-default-variant-id');
      var hasVariants = card.getAttribute('data-has-variants');
      if (defaultVariantId && hasVariants === 'false') {
        return defaultVariantId;
      }
    }

    return null;
  }

  /**
   * Perform a direct AJAX add-to-cart, then open the cart drawer.
   */
  function directAddToCart(variantId, quantity, button) {
    if (!variantId) return Promise.resolve();
    quantity = parseInt(quantity, 10) || 1;

    var sectionRoot = button.closest('[id^="shopify-section-"]');
    var sectionId = sectionRoot ? sectionRoot.id : 'vs-cart-drawer-integration';

    if (button) {
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    }

    var body = {
      items: [{ id: parseInt(variantId, 10), quantity: quantity }],
      sections: getSectionsToBundle(),
      sections_url: window.location.pathname
    };

    return fetch(window.Shopify ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, status: response.status, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok || (result.data.status && result.data.status >= 400)) {
          throw new Error((result.data && (result.data.description || result.data.message)) || 'Add to cart failed');
        }
        var addResponse = result.data;
        return fetchCart().then(function (cart) {
          notifyCartUpdated(cart, sectionId, addResponse);
          if (button) {
            button.classList.remove('is-loading');
            button.classList.add('is-success');
            button.removeAttribute('aria-busy');
            setTimeout(function () {
              if (button) {
                button.classList.remove('is-success');
                button.disabled = false;
              }
            }, 1200);
          }
        });
      })
      .catch(function (err) {
        if (button) {
          button.classList.remove('is-loading');
          button.removeAttribute('aria-busy');
          button.disabled = false;
          showButtonError(button, err && err.message);
        }
      });
  }

  /**
   * Tell Shopify which sections to re-render so the cart drawer + cart bubble update.
   */
  function getSectionsToBundle() {
    var ids = [];
    document.querySelectorAll('cart-drawer-component, cart-drawer, cart-icon, [data-cart-bubble]').forEach(function (el) {
      var sectionEl = el.closest('[id^="shopify-section-"]');
      if (sectionEl) {
        var id = sectionEl.id.replace('shopify-section-', '');
        if (ids.indexOf(id) === -1) ids.push(id);
      }
    });
    return ids.join(',');
  }

  /**
   * Identify a button that should be intercepted (product card / grid / collection quick-add).
   */
  function isInterceptableButton(target) {
    if (!target) return null;

    // Quick-add button inside a product card on a grid (not the main PDP form).
    var quickAddBtn = target.closest('.quick-add__button, [data-quick-add-button]');
    if (quickAddBtn && !target.closest('product-form-component, .product-information__form')) {
      // Only intercept when we can resolve a single variant
      if (getDirectAddVariantId(quickAddBtn)) return quickAddBtn;
    }

    // Any element that opts into direct-add via data attribute.
    var optIn = target.closest('[data-vs-direct-add]');
    if (optIn) return optIn;

    return null;
  }

  /**
   * Capture-phase click listener so we run BEFORE Horizon's quick-add modal handler.
   */
  document.addEventListener('click', function (event) {
    var btn = isInterceptableButton(event.target);
    if (!btn) return;

    var variantId = getDirectAddVariantId(btn);
    if (!variantId) return; // Let original behavior (modal) take over.

    // Stop the quick-add modal from opening for single-variant products.
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    var qty = parseInt(btn.getAttribute('data-quantity') || '1', 10);
    directAddToCart(variantId, qty, btn);
  }, { capture: true });

  /**
   * Optional: listen for our own cart updates to re-fetch cart bubble.
   * Horizon's cart drawer already handles this; included only for robustness.
   */
  document.addEventListener('cart:update', function () {
    // No-op placeholder; Horizon's cart drawer reacts to this event already.
  });
})();

/**
 * ============================================================================
 * VANITY BUNDLE BANNER — Cart Drawer Enhancement
 * ============================================================================
 * Detects line items added via the Vanity Bundle Deal section (they have
 * line-item property `_bundle_id`) and renders a premium "Bundle Discount
 * Applied" banner above the relevant items, grouping them visually.
 *
 * Compatibility:
 *   - Read-only on cart data; uses /cart.js to fetch items
 *   - Injects markup inside the existing cart drawer; non-destructive to native UI
 *   - Idempotent: removes + re-renders on each cart:update event
 *   - Pure JS — no Liquid edits to Horizon native files
 * ============================================================================
 */
(function () {
  'use strict';

  if (window.__vsBundleBannerLoaded) return;
  window.__vsBundleBannerLoaded = true;

  var BANNER_CLASS = 'vs-bundle-cart-banner';
  var GROUPED_ATTR = 'data-vs-bundle-grouped';

  function formatMoney(cents) {
    var currency = window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'EUR';
    return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: currency });
  }

  /**
   * Extract bundle groups from a cart.items array.
   * Returns { bundleId: { label, pct, savedTotal, items: [...] }, ... }
   */
  function groupBundleItems(items) {
    var groups = {};
    items.forEach(function (item) {
      var props = item.properties || {};
      var bundleId = props._bundle_id;
      if (!bundleId) return;

      if (!groups[bundleId]) {
        groups[bundleId] = {
          id: bundleId,
          label: props._bundle_label || 'Bundle Deal',
          pct: parseFloat(props._bundle_discount_pct) || 0,
          savedTotal: 0,
          items: []
        };
      }

      var savedCents = parseInt(props._bundle_saved_cents, 10) || 0;
      groups[bundleId].savedTotal += savedCents * (item.quantity || 1);
      groups[bundleId].items.push(item);
    });
    return groups;
  }

  /**
   * Build the bundle banner markup for a single group.
   */
  function buildBannerHTML(group) {
    var itemCount = group.items.length;
    var savedFormatted = formatMoney(group.savedTotal);

    return '' +
      '<div class="' + BANNER_CLASS + '" data-bundle-id="' + group.id + '">' +
        '<div class="' + BANNER_CLASS + '__row">' +
          '<span class="' + BANNER_CLASS + '__icon" aria-hidden="true">' +
            '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
              '<path d="M20 12v9H4v-9"/><path d="M22 7H2v5h20z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>' +
            '</svg>' +
          '</span>' +
          '<div class="' + BANNER_CLASS + '__main">' +
            '<p class="' + BANNER_CLASS + '__title">' +
              escapeHtml(group.label) +
              '<span class="' + BANNER_CLASS + '__pill">' + group.pct + '% OFF</span>' +
            '</p>' +
            '<p class="' + BANNER_CLASS + '__sub">' + itemCount + ' items in deze bundel</p>' +
          '</div>' +
          '<div class="' + BANNER_CLASS + '__savings">' +
            '<span class="' + BANNER_CLASS + '__savings-amount">-' + savedFormatted + '</span>' +
            '<span class="' + BANNER_CLASS + '__savings-label">bespaard</span>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Find all line item DOM nodes (Horizon renders them as <cart-items-component>
   * or inside .cart-items / .cart__item / [data-cart-line] etc.). Try multiple
   * selectors to be robust against Horizon's evolving markup.
   */
  function findLineItemNodes(root) {
    var selectors = [
      '[data-cart-item]',
      'cart-items-component .cart__item',
      '.cart-items .cart__item',
      '.cart__items > *',
      '.cart-item',
      'cart-line-component'
    ];
    for (var i = 0; i < selectors.length; i++) {
      var nodes = root.querySelectorAll(selectors[i]);
      if (nodes.length > 0) return nodes;
    }
    return [];
  }

  /**
   * Find the best container to inject the banner into.
   * We want it to appear ABOVE the line items but BELOW the cart heading
   * and any free-shipping progress bar.
   */
  function findBannerInjectionTarget(drawer) {
    // Try to find the cart items wrapper — banner goes just BEFORE it
    var lineItemsContainer = drawer.querySelector('cart-items-component') ||
                              drawer.querySelector('.cart-items') ||
                              drawer.querySelector('.cart__items') ||
                              drawer.querySelector('[data-cart-items]');
    return lineItemsContainer;
  }

  /**
   * Tag each cart line element with `data-vs-bundle-grouped="bundleId"`
   * so CSS can visually group them with a left border / spacing.
   */
  function tagLineItems(drawer, groups) {
    var nodes = findLineItemNodes(drawer);
    if (!nodes.length) return;

    // First clear any prior tagging
    nodes.forEach(function (n) {
      n.removeAttribute(GROUPED_ATTR);
    });

    // For each line node, look for a key/property hint we can read from the DOM.
    // Horizon serializes line items with keys like `properties[_bundle_id]`
    // or `data-line-key` etc. We need an item index to cross-reference cart.items.
    nodes.forEach(function (node, index) {
      // Try to match by index against the cart items array.
      // (Horizon renders line items in cart order, so this should match cart.items[index].)
      var bundleId = node.getAttribute('data-vs-line-bundle');
      if (bundleId) {
        node.setAttribute(GROUPED_ATTR, bundleId);
      }
    });
  }

  /**
   * Inject bundle banners into the cart drawer.
   */
  function renderBundleBanners(cart) {
    var drawer = document.querySelector('cart-drawer-component, cart-drawer');
    if (!drawer) return;

    // Remove any existing banners (re-render fresh)
    drawer.querySelectorAll('.' + BANNER_CLASS).forEach(function (el) {
      el.remove();
    });

    var groups = groupBundleItems(cart.items || []);
    var bundleIds = Object.keys(groups);
    if (bundleIds.length === 0) return;

    var target = findBannerInjectionTarget(drawer);
    if (!target) return;

    // Tag matching line items with bundle id so CSS can style them grouped.
    // Match by cart-line key (Horizon line items expose this via `data-line-key`
    // or similar). We find cart items by line key + tag matching DOM nodes.
    var keyToBundleId = {};
    (cart.items || []).forEach(function (item) {
      var bid = item.properties && item.properties._bundle_id;
      if (bid && item.key) keyToBundleId[item.key] = bid;
    });

    findLineItemNodes(drawer).forEach(function (node) {
      // Try common attribute patterns for line keys
      var lineKey =
        node.getAttribute('data-line-key') ||
        node.getAttribute('data-key') ||
        node.getAttribute('data-cart-line-key') ||
        (node.querySelector('[data-line-key]') && node.querySelector('[data-line-key]').getAttribute('data-line-key')) ||
        (node.querySelector('input[name="id"]') && node.querySelector('input[name="id"]').value);

      if (lineKey && keyToBundleId[lineKey]) {
        node.setAttribute(GROUPED_ATTR, keyToBundleId[lineKey]);
      }
    });

    // Inject banners (one per bundle group), prepended to the items container
    bundleIds.forEach(function (bid) {
      var html = buildBannerHTML(groups[bid]);
      target.insertAdjacentHTML('beforebegin', html);
    });
  }

  /**
   * Main: re-render on every cart update, and on initial drawer open.
   */
  function refreshFromCart() {
    fetch(window.Shopify ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) { renderBundleBanners(cart); })
      .catch(function (err) { console.warn('[vs-bundle-banner] cart fetch failed', err); });
  }

  // Listen for cart updates (Horizon's standard event)
  document.addEventListener('cart:update', function (e) {
    // Slight delay so Horizon re-renders the drawer DOM first
    setTimeout(refreshFromCart, 100);
  });

  // Also re-render whenever the drawer opens (e.g. user clicks cart icon)
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-cart-trigger], [aria-controls*="cart-drawer"], a[href="/cart"]');
    if (trigger) {
      setTimeout(refreshFromCart, 250);
    }
  });

  // First load — render bundle banner if cart already has bundle items (returning visitor)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshFromCart);
  } else {
    refreshFromCart();
  }
})();